<script>
    import { teal, defaults, purple, orange } from './utils/colors';
    import Legend from './components/Legend.svelte';
    import pkg from 'mapbox-gl';
    import 'mapbox-gl/dist/mapbox-gl.css';
    import { untrack } from 'svelte';
    const { Map } = pkg;

    let flexContainer = $state(null);
    const ninos = [
        {
            tileset: 'mapbox://cnndigital.ssta_anom_19820901',
            date: '1982'
        },
        {
            tileset: 'mapbox://cnndigital.ssta_anom_19970901',
            date: '1997'
        }, {
            tileset: 'mapbox://cnndigital.ssta_anom_20150901',
            date: '2015'
        }
    ]

    const currentNino = {
        tileset: 'mapbox://cnndigital.ssta_anom_20260901',
        date: '2026'
    }

    const REGION = { w: -170, e: -120, s: -5, n: 5 };
    const ninoBox = (() => {
        const { w, e, s, n } = REGION;
        const step = 2;
        const pts = [];
        for (let lon = w; lon <= e; lon += step) pts.push([lon, s]);
        for (let lat = s; lat <= n; lat += step) pts.push([e, lat]);
        for (let lon = e; lon >= w; lon -= step) pts.push([lon, n]);
        for (let lat = n; lat >= s; lat -= step) pts.push([w, lat]);
        pts.push([w, s]);
        return { type: 'Polygon', coordinates: [pts.reverse()] };
    })();

    const createNinoMap = (nino, container, zoom, showLabels = false) => {
        const map = new Map({
            accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
            container,
            style: "mapbox://styles/cnndigital/cmtx3kkoo000101qm8j038xz4",
            projection: 'globe',
            zoom,
            interactive: false,
            scrollZoom: false,
            attributionControl: false,
            center: [-125, 0]
        });
        map.on('load', () => {
            // Hide all label (symbol) layers from the base style,
            // unless this globe should show them.
            if (!showLabels) {
                map.getStyle().layers.forEach((layer) => {
                    if (layer.type === 'symbol') {
                        map.setLayoutProperty(layer.id, 'visibility', 'none');
                    }
                });
            }

            map.addSource('ssta-raster', {
                type: 'raster',
                url: nino.tileset
            });
            map.addLayer({
                id: 'ssta-raster',
                type: 'raster',
                source: 'ssta-raster'
            });

            map.addSource('nino-box', {
                type: 'geojson',
                data: ninoBox
            });
            map.addLayer({
                id: 'nino-box',
                source: 'nino-box',
                type: 'line',
                paint: {
                    'line-color': '#333333',
                    'line-width': 1
                }
            })
        })
        

        return map;
    }

    // Keep the 2026 globe zoom in sync with the layout breakpoint:
    // smaller (former) zoom on mobile, larger on desktop.
    const mobileMq = '(max-width: 639px)';
    const currentZoom = () => (window.matchMedia(mobileMq).matches ? 0.85 : 1.3);

    $effect(() => {
        if (flexContainer) {
            untrack(() => flexContainer);
            ninos.forEach((n) => createNinoMap(n, `n-${n.date}`, 0.4));

            const currentMap = createNinoMap(currentNino, 'current-nino-map', currentZoom(), true);
            const mq = window.matchMedia(mobileMq);
            const updateZoom = () => currentMap.setZoom(currentZoom());
            mq.addEventListener('change', updateZoom);
        }
    })
</script>

<Legend />
<div class="graphic">
    <div class="current-nino">
        <h3 class="current-year">2026</h3>
        <div class="current-nino-map" id="current-nino-map">
            <span class="nino-label">Niño 3.4 Region</span>
        </div>
    </div>
    <div class="comparison-flex" bind:this={flexContainer}>
        {#each ninos as n}
            <div class="comparison-item">
                <div class="map-container" id="n-{n.date}">
                    <h3 class="year-label">{n.date}</h3>
                </div>
            </div>
        {/each}
    </div>

</div>

<style lang='scss'>
h3 {
    margin-bottom: 0;
}

    .year-label {
        position: absolute;
        top: 8px;
        left: 8px;
        z-index: 1;
        margin: 0;
        pointer-events: none;
        font-family: $sans-serif;
        font-weight: 700;
        color: #333333;
        text-shadow:
            0 0 2px #ffffff,
            0 0 2px #ffffff,
            0 0 2px #ffffff;
    }

    .current-nino {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 20px;

        @media #{$desktop} {
            align-items: center;
        }

        .current-year {
            margin: 0 0 4px;
            font-family: $sans-serif;
            font-weight: 700;
        }

        .current-nino-map {
            width: 100%;
            max-width: 340px;
            aspect-ratio: 1/1;
            position: relative;

            @media #{$desktop} {
                max-width: 450px;
            }

            .nino-label {
                position: absolute;
                top: 42%;
                left: 35%;
                transform: translate(-50%, -100%);

                @media #{$desktop} {
                    top: 44%;
                }
                z-index: 1;
                pointer-events: none;
                white-space: nowrap;
                font-family: $sans-serif;
                font-weight: 500;
                font-size: 14px;
                color: #333333;
                text-shadow:
                    0 0 2px #ffffff,
                    0 0 2px #ffffff,
                    0 0 2px #ffffff;
            }
        }
    }

    .comparison-flex {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 10px;

        @media #{$mobile} {
            grid-template-columns: 1fr;
        }

        .map-container {
            width: 100%;
            aspect-ratio: 1/1;
            position: relative;

            @media #{$mobile} {
                max-width: 250px;
                margin: 0 auto;
            }
        }
    }
    :global {
        .mapboxgl-control-container {
            display: none;
        }
    }
</style>
